import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  savedSearchSchema,
  type SavedSearch,
  type SavedSearchEntityType,
  type SavedSearchInput,
} from '../types/saved-search';
import { useAuth } from './useAuth';

interface UseSavedSearchesResult {
  searches: SavedSearch[];
  loading: boolean;
  error: Error | null;
  createSearch: (input: SavedSearchInput) => Promise<SavedSearch>;
  updateSearch: (id: string, input: SavedSearchInput) => Promise<SavedSearch>;
  deleteSearch: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

function parseSavedSearch(value: unknown): SavedSearch {
  return savedSearchSchema.parse(value);
}

export function useSavedSearches(entityType: SavedSearchEntityType): UseSavedSearchesResult {
  const { user } = useAuth();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setSearches([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('entity_type', entityType)
        .order('name', { ascending: true });

      if (queryError) throw queryError;
      // Valider ligne par ligne : une définition malformée (ex. vue partagée
      // par un autre client) est ignorée sans effacer toutes les autres vues.
      setSearches(
        (data || []).reduce<SavedSearch[]>((acc, row: unknown) => {
          try {
            acc.push(parseSavedSearch(row));
          } catch (parseError) {
            console.warn('[useSavedSearches] recherche ignorée (définition invalide):', parseError);
          }
          return acc;
        }, [])
      );
    } catch (caughtError) {
      const normalizedError =
        caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      setError(normalizedError);
      setSearches([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createSearch = useCallback(
    async (input: SavedSearchInput) => {
      if (!user) throw new Error('Utilisateur non authentifié.');

      const { data, error: insertError } = await supabase
        .from('saved_searches')
        .insert({
          owner_user_id: user.id,
          entity_type: entityType,
          name: input.name.trim(),
          is_shared: input.isShared,
          definition: input.definition,
          definition_version: 1,
        })
        .select('*')
        .single();

      if (insertError) throw insertError;
      const created = parseSavedSearch(data);
      setSearches((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      return created;
    },
    [entityType, user]
  );

  const updateSearch = useCallback(
    async (id: string, input: SavedSearchInput) => {
      if (!user) throw new Error('Utilisateur non authentifié.');

      const { data, error: updateError } = await supabase
        .from('saved_searches')
        .update({
          name: input.name.trim(),
          is_shared: input.isShared,
          definition: input.definition,
          definition_version: 1,
        })
        .eq('id', id)
        .eq('owner_user_id', user.id)
        .select('*')
        .single();

      if (updateError) throw updateError;
      const updated = parseSavedSearch(data);
      setSearches((current) =>
        current
          .map((search) => (search.id === id ? updated : search))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      return updated;
    },
    [user]
  );

  const deleteSearch = useCallback(
    async (id: string) => {
      if (!user) throw new Error('Utilisateur non authentifié.');

      const { error: deleteError } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', id)
        .eq('owner_user_id', user.id);

      if (deleteError) throw deleteError;
      setSearches((current) => current.filter((search) => search.id !== id));
    },
    [user]
  );

  return { searches, loading, error, createSearch, updateSearch, deleteSearch, refresh };
}
